import algosdk from "algosdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWallet } from "../context/WalletContext";
import { useTransaction } from "../context/TransactionContext";
import "../styles/target.css";
import "../styles/general.css";

interface TargetProps {
  algodClient: algosdk.Algodv2;
  setMessage: (msg: string) => void;
  account: string;
  username: string;
  algoBalance: number;
  umqBalance: number;
  logout: () => void;
}

const Target: React.FC<TargetProps> = ({ algodClient, setMessage }) => {
  const navigate = useNavigate();
  const { account } = useWallet();
  const { openTransactionModal } = useTransaction();
  const appId = 749051702;
  const appAddress = "3HZJBRNKU32BQCECPSPL65YJTATPZOMHX23EUXAFDXYZMZAFBRFNTKUQXY";
  const asaId = 748884896;

  const [hasOptedIn, setHasOptedIn] = useState<boolean>(false);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [playing, setPlaying] = useState(false);
  const [canClaim, setCanClaim] = useState(false);
  const [position, setPosition] = useState({ top: 100, left: 100 });
  const [velocity, setVelocity] = useState({ dx: 2, dy: 2 });
  const [elapsedTime, setElapsedTime] = useState(0);
  const gameZoneRef = useRef<HTMLDivElement>(null);
  const [startTime, setStartTime] = useState<number | null>(null);

  const optInToApp = useCallback(async () => {
    try {
      if (!account?.sk || !account?.addr) throw new Error("Wallet no conectada");
  
      const suggestedParams = await algodClient.getTransactionParams().do();
      const optInTxn = algosdk.makeApplicationOptInTxn(account.addr, suggestedParams, appId);
  
      const signedTxn = optInTxn.signTxn(account.sk);
      const { txId } = await algodClient.sendRawTransaction(signedTxn).do();
  
      setMessage(`Opt-in exitoso. TxID: ${txId}`);
      setHasOptedIn(true);
    } catch (err) {
      console.error(err);
      setMessage("Error al hacer opt-in");
    }
  }, [account, algodClient, appId, setMessage]);

  useEffect(() => {
    if (!playing) return;

    const interval = setInterval(() => {
      const maxWidth = gameZoneRef.current?.offsetWidth || 300;
      const maxHeight = gameZoneRef.current?.offsetHeight || 300;

      setPosition({
        top: Math.random() * (maxHeight - 100),
        left: Math.random() * (maxWidth - 100),
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [playing]);

  const hitSound = useRef<HTMLAudioElement | null>(null);
  const missSound = useRef<HTMLAudioElement | null>(null);
  const startSound = useRef<HTMLAudioElement | null>(null);
  
  const handleMiss = () => {
  if (!playing) return;
  missSound.current?.play();
  setLives(prev => {
    const newLives = prev - 1;
    if (newLives <= 0) {
      setPlaying(false);
      setMessage(score > 0 
        ? "🎯 Puedes reclamar tu recompensa." 
        : "😶 No hiciste puntos, Suerte en tu próximo intento."
      );
      setCanClaim(true);
    }
    return newLives;
  });
};

  const handleHit = (e: React.MouseEvent) => {
  e.stopPropagation();
  if (!playing) return;
  hitSound.current?.play();
  setScore(prev => prev + 1);
};

 const handleStart = () => {
  openTransactionModal({
    recipient: appAddress,
    amount: 100,
    asset: { name: "UMQ", id: asaId, decimals: 9 },
    onConfirm: async () => {
      startSound.current?.play();
      setScore(0);
      setLives(3);
      setPlaying(true);
      setCanClaim(false);
      setStartTime(Date.now());
    },
  });
};

  const handleClaimReward = async () => {
  try {
    if (!account?.sk) throw new Error("Cuenta inválida o no cargada.");

    const suggestedParams = await algodClient.getTransactionParams().do();

    const dec = BigInt(1e9);
    const FEE_AMOUNT = 15n * dec;
    const PAYMENT_AMOUNT = 100n * dec;
    const appAddress = algosdk.getApplicationAddress(appId);

    // 1️⃣ Transacción de fee (15 UMQ)
    const feeTxn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
      from: account.addr,
      to: appAddress,
      assetIndex: asaId,
      amount: FEE_AMOUNT,
      suggestedParams,
    });

    // 2️⃣ Transacción de pago base (100 UMQ)
    const paymentTxn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
      from: account.addr,
      to: appAddress,
      assetIndex: asaId,
      amount: PAYMENT_AMOUNT,
      suggestedParams,
    });

    // 3️⃣ Transacción de reclamo (claim)
    const appCallTxn = algosdk.makeApplicationNoOpTxnFromObject({
      from: account.addr,
      appIndex: appId,
      appArgs: [
        new Uint8Array(Buffer.from("claim")),
        algosdk.encodeUint64(score), // los aciertos
      ],
      accounts: [account.addr],
      foreignAssets: [asaId],
      suggestedParams,
    });

    // 🔗 Agrupar las tres transacciones
    algosdk.assignGroupID([feeTxn, paymentTxn, appCallTxn]);

    // ✍️ Firmar y enviar
    const signedTxns = [
      feeTxn.signTxn(account.sk),
      paymentTxn.signTxn(account.sk),
      appCallTxn.signTxn(account.sk),
    ];

    const { txId } = await algodClient.sendRawTransaction(signedTxns).do();

    setMessage(`🎯 Recompensa reclamada correctamente. TxID: ${txId}`);
    setCanClaim(false);
  } catch (err: any) {
    console.error("Error al reclamar recompensa:", err);
    setMessage("Error al reclamar recompensa: " + err.message);
  }
};

  useEffect(() => {
    if (!playing) return;

    let animationId: number;

    const move = () => {
      setPosition(prev => {
        const zone = gameZoneRef.current;
        if (!zone) return prev;

        const width = zone.offsetWidth;
        const height = zone.offsetHeight;

        const { top, left } = prev;
        let { dx, dy } = velocity;

        if (left + dx < 0 || left + 100 + dx > width) dx *= -1;
        if (top + dy < 0 || top + 100 + dy > height) dy *= -1;

        setVelocity({ dx, dy });

        return {
          top: top + dy,
          left: left + dx,
        };
      });

      animationId = requestAnimationFrame(move);
    };

    animationId = requestAnimationFrame(move);
    return () => cancelAnimationFrame(animationId);
  }, [playing, velocity]);
  

  useEffect(() => {
  const checkOptIn = async () => {
    if (!account?.addr) return;

    try {
      const appInfo = await algodClient.accountApplicationInformation(account.addr, appId).do();
      if (appInfo['app-local-state']) {
        setHasOptedIn(true);
      }
    } catch (err) {
      console.warn("No opt-in aún o error verificando:", err);
    }
  };

  checkOptIn();
}, [account?.addr, algodClient]);

useEffect(() => {
  hitSound.current = new Audio("/sounds/hit.wav");
  missSound.current = new Audio("/sounds/miss.wav");
  startSound.current = new Audio("/sounds/start.wav");
}, []);

  useEffect(() => {
  let interval: NodeJS.Timeout;
  if (playing && startTime !== null) {
    interval = setInterval(() => {
      setElapsedTime(Date.now() - startTime);
    }, 100);
  } 

  return () => clearInterval(interval);
}, [playing, startTime]);

  return (
    <div className="modal-target">
      <div className="target-game tribal-bg">
        <audio ref={hitSound} src="/sounds/hit.wav" preload="auto" />
        <audio ref={missSound} src="/sounds/miss.wav" preload="auto" />
        <audio ref={startSound} src="/sounds/start.wav" preload="auto" />
        <h1>🏹 Tiro al Blanco Holkan</h1>
         <div className="game-zone" ref={gameZoneRef} onClick={handleMiss}>
          <div
  className="target"
  onClick={handleHit}
  style={{ top: position.top, left: position.left }}
/>
        </div>
        <h4> Puntaje: {score} 🎯 — Tiempo: {(elapsedTime / 1000).toFixed(1)}s ⏱️</h4>
        <h4>Vidas: {"❤️".repeat(lives)}{lives === 0 && " 💀"}</h4>

{!hasOptedIn ? (
  <>
    <button disabled={false}className="button-primary" onClick={optInToApp}>
      Iniciar cacería
    </button>
  </>
) : (
  <>
    {!playing && !canClaim && (
      <>
        <button
          className="button-primary"
          onClick={handleStart}
          disabled={playing}
        >
          Empezar Juego
        </button>
      </>
    )}

    {!playing && canClaim && (
  <>
    {score > 0 ? (
      <button className="button-primary" onClick={handleClaimReward}>
        Recompensa
      </button>
    ) : (
      <button className="button-secondary" onClick={handleStart}>
        Intentar de Nuevo
      </button>
      )}
      </>
    )}
  </>
)}

<button className="button-exit" onClick={() => navigate("/home")}>
  Volver
</button>
      </div>
    </div>
  );
};

export default Target;